import React from 'react';
import { CheckCircle2, Sparkles, Film, Mic2, AlertTriangle, RotateCcw, ArrowLeft } from 'lucide-react';

export type BakerStageId = 'gather' | 'split' | 'hear';

interface SoulStreamLoaderProps {
  stage: BakerStageId;
  clipTitle: string;
  transferDone: boolean;
  splitDone: boolean;
  hearDone: boolean;
  isLoadingDialogue: boolean;
  transferStatusText?: string;
  transferProgress?: number;
  bakeTimeoutError?: string | null;
  onRetryBake?: () => void;
  onBackToCapture?: () => void;
  onSkipHear?: () => void;
}

interface StageRow {
  id: BakerStageId;
  icon: React.ReactNode;
  title: string;
  working: string;
  done: string;
}

const STAGE_ROWS: StageRow[] = [
  {
    id: 'gather',
    icon: <Film size={18} />,
    title: 'Gathering the Soul',
    working: 'Gathering the soul from its captured moment…',
    done: 'Soul captured'
  },
  {
    id: 'split',
    icon: <Sparkles size={18} />,
    title: 'Splitting the Soul',
    working: 'Splitting the soul — freeing the voice from the score…',
    done: 'Voice & score unbound'
  },
  {
    id: 'hear',
    icon: <Mic2 size={18} />,
    title: 'Hearing the Voice',
    working: 'Listening for voices in the silence…',
    done: 'Voices mapped'
  }
];

export const SoulStreamLoader: React.FC<SoulStreamLoaderProps> = ({
  stage,
  clipTitle,
  transferDone,
  splitDone,
  hearDone,
  isLoadingDialogue,
  transferStatusText,
  transferProgress,
  bakeTimeoutError,
  onRetryBake,
  onBackToCapture,
  onSkipHear
}) => {
  const doneMap: Record<BakerStageId, boolean> = {
    gather: transferDone,
    split: splitDone,
    hear: hearDone
  };

  const progressSegments: BakerStageId[] = ['gather', 'split', 'hear'];

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '18px',
        padding: '1.75rem',
        textAlign: 'center',
        background: 'radial-gradient(ellipse at center, rgba(30, 27, 75, 0.72) 0%, rgba(10, 15, 30, 0.98) 100%)',
        backdropFilter: 'blur(10px)',
        zIndex: 35
      }}
    >
      {/* Title */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
        <div
          className="badge badge-glow-purple"
          style={{ fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase' }}
        >
          <Sparkles size={12} />
          <span>SoulSyncing</span>
        </div>
        <span style={{ fontSize: '1.02rem', fontWeight: 800, color: '#f8fafc', maxWidth: '400px', lineHeight: 1.35 }}>
          {clipTitle}
        </span>
      </div>

      {/* Segmented progress bar */}
      <div
        style={{
          width: 'min(340px, 78%)',
          display: 'flex',
          gap: '6px',
          height: '7px'
        }}
      >
        {progressSegments.map((segId) => {
          const isDone = doneMap[segId];
          const isActive = stage === segId;
          return (
            <div
              key={segId}
              style={{
                flex: 1,
                height: '100%',
                borderRadius: 'var(--radius-full)',
                background: isDone
                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                  : isActive
                  ? 'linear-gradient(90deg, #8b5cf6, #06b6d4)'
                  : 'rgba(255,255,255,0.12)',
                boxShadow: isActive ? '0 0 12px rgba(139, 92, 246, 0.6)' : 'none',
                animation: isActive ? 'soulStreamPulse 1.4s ease-in-out infinite' : 'none'
              }}
            />
          );
        })}
      </div>

      {/* Stage rows */}
      <div
        style={{
          width: 'min(400px, 88%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        {STAGE_ROWS.map((row) => {
          const isDone = doneMap[row.id];
          const isActive = stage === row.id;
          const isPending = !isDone && !isActive;

          return (
            <div
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '0.7rem 0.9rem',
                borderRadius: 'var(--radius-md)',
                background: isActive
                  ? 'rgba(139, 92, 246, 0.14)'
                  : isDone
                  ? 'rgba(16, 185, 129, 0.08)'
                  : 'rgba(255, 255, 255, 0.03)',
                border: isActive
                  ? '1px solid rgba(139, 92, 246, 0.45)'
                  : isDone
                  ? '1px solid rgba(16, 185, 129, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.07)',
                boxShadow: isActive ? '0 0 18px rgba(139, 92, 246, 0.22)' : 'none',
                opacity: isPending ? 0.55 : 1,
                textAlign: 'left'
              }}
            >
              {/* Icon / spinner / check */}
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  flexShrink: 0,
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDone
                    ? 'rgba(16, 185, 129, 0.18)'
                    : isActive
                    ? 'rgba(139, 92, 246, 0.22)'
                    : 'rgba(255,255,255,0.06)',
                  color: isDone ? '#34d399' : isActive ? '#c4b5fd' : 'var(--text-muted)'
                }}
              >
                {isDone ? (
                  <CheckCircle2 size={18} />
                ) : isActive ? (
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(139, 92, 246, 0.25)',
                      borderTopColor: '#c084fc',
                      borderRadius: '50%',
                      animation: 'spin 0.9s linear infinite'
                    }}
                  />
                ) : (
                  row.icon
                )}
              </div>

              {/* Stage label + copy */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                <span
                  style={{
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: isDone ? '#6ee7b7' : isActive ? '#e9d5ff' : 'var(--text-muted)'
                  }}
                >
                  {row.title}
                </span>
                <span
                  style={{
                    fontSize: '0.78rem',
                    color: 'var(--text-muted)',
                    lineHeight: 1.35,
                    maxWidth: '300px'
                  }}
                >
                  {isDone
                    ? row.done
                    : (row.id === 'gather' && transferStatusText)
                    ? transferStatusText
                    : row.working}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Timeout / YouTube block alert box */}
      {bakeTimeoutError && (
        <div
          style={{
            width: 'min(400px, 88%)',
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            textAlign: 'left'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#fca5a5' }}>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
              <strong style={{ display: 'block', color: '#fecaca', marginBottom: '2px' }}>
                Capture Stream Blocked
              </strong>
              {bakeTimeoutError}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '2px' }}>
            {onBackToCapture && (
              <button
                type="button"
                onClick={onBackToCapture}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: '#e2e8f0',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <ArrowLeft size={12} />
                <span>Return to Capture</span>
              </button>
            )}
            {onRetryBake && (
              <button
                type="button"
                onClick={onRetryBake}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                }}
              >
                <RotateCcw size={12} />
                <span>Check Readiness Again</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Skip Diarization Option if in 'hear' stage or if it's taking time */}
      {stage === 'hear' && onSkipHear && (
        <button
          type="button"
          onClick={onSkipHear}
          style={{
            background: 'rgba(139, 92, 246, 0.16)',
            color: '#c4b5fd',
            border: '1px solid rgba(139, 92, 246, 0.35)',
            borderRadius: 'var(--radius-full)',
            padding: '6px 16px',
            fontSize: '0.76rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 10px rgba(139, 92, 246, 0.2)'
          }}
        >
          <Sparkles size={12} />
          <span>Skip voice detection & enter Studio</span>
        </button>
      )}

      {/* Footer hint */}
      <span
        style={{
          fontSize: '0.74rem',
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.03em'
        }}
      >
        {isLoadingDialogue && stage === 'hear'
          ? 'listening for speech in isolated vocals…'
          : bakeTimeoutError
          ? 'Neural stem separation is taking longer than expected.'
          : 'this takes but a heartbeat or three…'}
      </span>
    </div>
  );
};
